import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useConfirm } from '../../lib/confirm'
import { titleCase } from '../../lib/format'
import { Alert, Badge, Button, Card, CardBody, EmptyState, Skeleton } from '../../components/ui'
import { PlusIcon, UsersGroupIcon } from '../../components/icons'

/* Faithful port of anansi/app/templates/admin/roles.html + its view
 * admin/routes.py:roles(). The CAPABILITY-based Role Builder
 * (role_service.py, RTDB custom_roles) - drives authorization/capability
 * checks. Distinct from the statutory Role Catalog (RoleCatalog.tsx). */

interface Capability { key: string; group: string; label: string }
interface BuilderRole {
  key: string; title: string; description?: string; tier: string; scope: string
  level: number; caps: string[]; system: boolean; active: boolean
}
interface RolesResponse { ok: boolean; items: BuilderRole[]; counts: Record<string, number> }
interface MetaResponse { ok: boolean; capabilities: Capability[] }

export default function AdminRoles() {
  const qc = useQueryClient()
  const confirm = useConfirm()
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: () => api.get<RolesResponse>('/admin/roles'),
  })
  const { data: meta } = useQuery({
    queryKey: ['admin', 'roles', 'meta'],
    queryFn: () => api.get<MetaResponse>('/admin/roles/meta'),
  })
  const capLabels = new Map((meta?.capabilities ?? []).map((c) => [c.key, c.label]))

  const del = useMutation({
    mutationFn: (key: string) => api.del(`/admin/roles/${key}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'roles'] }),
  })

  const roles = data?.items ?? []
  const counts = data?.counts ?? {}

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Role Builder</h1>
          <p className="page-head__sub">Custom capability-based roles - what each role can actually do in the app.</p>
        </div>
        <Link to="/roles/new" className="btn btn--primary"><PlusIcon size={16} /> New role</Link>
      </div>

      {del.isError && (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <Alert tone="danger">{del.error instanceof ApiError ? del.error.message : 'Could not delete that role.'}</Alert>
        </div>
      )}

      {isError ? (
        <Alert tone="danger">{(error as Error).message}</Alert>
      ) : isLoading ? (
        <div className="stack"><Skeleton h={110} /><Skeleton h={110} /></div>
      ) : roles.length === 0 ? (
        <EmptyState icon={<UsersGroupIcon size={22} />} title="No roles yet" body="" />
      ) : (
        <div className="stack">
          {roles.map((r) => (
            <Card key={r.key}>
              <CardBody>
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--sp-3)' }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="row" style={{ gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
                      <strong>{r.title}</strong>
                      <span style={{ fontFamily: 'monospace', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{r.key}</span>
                      <Badge tone={r.system ? 'info' : 'neutral'}>{r.system ? 'Core' : 'Custom'}</Badge>
                      {!r.active && <Badge tone="danger">Inactive</Badge>}
                    </div>
                    {r.description && (
                      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', margin: '4px 0' }}>{r.description}</p>
                    )}
                    <div className="row" style={{ gap: 'var(--sp-2)', flexWrap: 'wrap', marginTop: 6 }}>
                      <Badge tone="neutral">tier: {titleCase(r.tier)}</Badge>
                      <Badge tone="neutral">scope: {titleCase(r.scope)}</Badge>
                      <Badge tone="neutral">level: {r.level}</Badge>
                      <Badge tone="neutral">{counts[r.key] ?? 0} user(s)</Badge>
                    </div>
                    <div className="row" style={{ gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                      {r.caps.includes('*') ? (
                        <Badge tone="warning">All capabilities</Badge>
                      ) : r.caps.length > 0 ? (
                        r.caps.map((c) => <Badge key={c} tone="info">{capLabels.get(c) || c}</Badge>)
                      ) : (
                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>No capabilities</span>
                      )}
                    </div>
                  </div>
                  <div className="row" style={{ gap: 'var(--sp-2)', flexShrink: 0 }}>
                    {r.system ? (
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>Locked</span>
                    ) : (
                      <>
                        <Link to={`/roles/${r.key}/edit`} className="btn btn--secondary btn--sm">Edit</Link>
                        <Button
                          variant="danger" size="sm" loading={del.isPending && del.variables === r.key}
                          onClick={async () => {
                            if (await confirm({
                              title: `Delete role '${r.title}'?`,
                              message: 'This cannot be undone.',
                              destructive: true,
                            })) del.mutate(r.key)
                          }}
                        >
                          Delete
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </>
  )
}
